// The one speech bubble on an order Clyde placed (orders.source = 'sms_agent').
//
// Hover: the summary that became the order (the recap the buyer or rep said yes
// to, or Clyde's confirmation; older orders, the buyer's text), fetched on demand
// from GET /orders/:id/agent-message — one bounded, org-scoped message, no items,
// no account row — and only once the card opens. Its title, "Placed by text",
// links to the thread like the bubble itself: the console, on that thread,
// scrolled to that message. Every other source renders the plain badge.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { MessageSquare } from "lucide-react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { OrderSourceBadge } from "@/components/order-source-badge";
import { useAdminBasePath } from "@/lib/adminPath";
import { consoleMessagePath } from "@/lib/clydeThread";
import { getApiPath } from "@/lib/queryClient";

/** What GET /orders/:id/agent-message answers. */
export interface OrderAgentMessage {
  orderId: number;
  conversationId: number;
  messageId: number | null;
  message: { id: number; role: string; content: string; createdAt: string } | null;
}

export interface OrderSourceMarkOrder {
  id: number;
  source?: string | null;
  agentConversationId?: number | null;
  agentMessageId?: number | null;
}

const HOVER_MAX_CHARS = 400;

export function OrderSourceMark({
  order,
  label,
  onNavigate,
  plain = false,
  size = 14,
  className,
}: {
  order: OrderSourceMarkOrder;
  /** Text beside the bubble ("View conversation" on the order slip); icon only without it. */
  label?: string;
  /** Runs before the jump to the thread — the slip closes its dialog. */
  onNavigate?: () => void;
  /** The plain source badge, no link or preview (a customer's read-only slip). */
  plain?: boolean;
  size?: number;
  className?: string;
}) {
  const isTextOrder = order.source === "sms_agent" && order.agentConversationId != null;
  if (!isTextOrder || plain) return <OrderSourceBadge source={order.source} size={size} className={className} />;
  return <AgentOrderBubble order={order} label={label} onNavigate={onNavigate} size={size} className={className} />;
}

function AgentOrderBubble({
  order,
  label,
  onNavigate,
  size,
  className,
}: {
  order: OrderSourceMarkOrder;
  label?: string;
  onNavigate?: () => void;
  size: number;
  className?: string;
}) {
  const [, setLocation] = useLocation();
  const adminBase = useAdminBasePath();
  const [open, setOpen] = useState(false);
  const path = `/api/orders/${order.id}/agent-message`;
  const query = useQuery<OrderAgentMessage>({
    queryKey: [getApiPath(path)],
    enabled: open,
    staleTime: 60_000,
  });
  const conversationId = query.data?.conversationId ?? order.agentConversationId!;
  const messageId = query.data?.messageId ?? order.agentMessageId ?? null;
  const href = consoleMessagePath(adminBase, conversationId, messageId);
  const text = query.data?.message?.content ?? "";
  const shown = text.length > HOVER_MAX_CHARS ? `${text.slice(0, HOVER_MAX_CHARS)}…` : text;
  const go = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    onNavigate?.();
    setLocation(href);
  };

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={go}
          // The slip's dialog focuses this button as it opens; only keyboard focus
          // should open the preview (Radix skips its handler on preventDefault).
          onFocus={(e) => {
            if (!e.currentTarget.matches(":focus-visible")) e.preventDefault();
          }}
          className={
            label
              ? `inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-violet-700 hover:bg-violet-50 hover:text-violet-900 ${className ?? ""}`
              : `inline-flex items-center text-violet-600 hover:text-violet-800 ${className ?? ""}`
          }
          aria-label={label ? undefined : "View conversation"}
          data-testid="order-agent-bubble"
          data-source="sms_agent"
        >
          <MessageSquare size={size} strokeWidth={2} />
          {label}
        </button>
      </HoverCardTrigger>
      {/* Portalled: the slip's dialog clips its own overflow, and the bubble sits at its right edge. */}
      <HoverCardPrimitive.Portal>
      <HoverCardContent className="w-72 text-sm" align="end" collisionPadding={12} onClick={(e) => e.stopPropagation()}>
        <a
          href={href}
          onClick={go}
          className="text-xs font-medium text-violet-700 hover:text-violet-900 hover:underline"
          data-testid="order-agent-bubble-title"
        >
          Placed by text
        </a>
        {query.isLoading ? (
          <p className="mt-1 text-xs text-muted-foreground">Loading the message…</p>
        ) : query.isError ? (
          <p className="mt-1 text-xs text-muted-foreground">Couldn't load the message.</p>
        ) : query.data?.message ? (
          <p className="mt-1 whitespace-pre-wrap break-words text-gray-800" data-testid="order-agent-bubble-text">{shown}</p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">The order came from this thread; its message is no longer on file.</p>
        )}
      </HoverCardContent>
      </HoverCardPrimitive.Portal>
    </HoverCard>
  );
}
